import { createSseParser } from './sseParser.ts'

export type AlertStreamStatus =
    | 'connecting'
    | 'connected'
    | 'retrying'
    | 'unavailable'
    | 'denied'
    | 'stopped'

export interface AlertStreamTiming {
    /** 이 시간 동안 바이트(ping 포함)가 없으면 끊고 다시 접속한다 */
    watchdogMs: number
    backoffBaseMs: number
    backoffMaxMs: number
    /** 서버가 스트림을 거절(404·429·503)했을 때 다시 시도하기까지의 최소 대기 */
    unavailableDelayMs: number
    /** 서버가 `reconnect` 로 정상 종료했을 때 다시 붙기 전 무작위 대기의 상한 */
    reconnectJitterMs: number
    /** 이 시간 이상 유지된 연결이어야 실패 횟수를 되돌린다(즉시 끊기는 연결의 재시도 폭주 방지) */
    stableConnectionMs: number
}

export const DEFAULT_ALERT_STREAM_TIMING: AlertStreamTiming = {
    watchdogMs: 45_000,
    backoffBaseMs: 1_000,
    backoffMaxMs: 30_000,
    unavailableDelayMs: 60_000,
    reconnectJitterMs: 2_000,
    stableConnectionMs: 10_000
}

export interface AlertStreamClientOptions {
    url: string
    /** 연결 시점의 액세스 토큰. 비어 있으면 로그아웃 상태로 보고 접속하지 않는다 */
    getToken: () => string
    /** 401 이면 호출한다. 새 토큰을 돌려주거나 실패하면 null */
    refreshToken: () => Promise<string | null>
    /** 갱신된 토큰을 앱 상태에 반영한다 */
    onToken: (token: string) => void
    onEvent: (event: string, data: unknown) => void
    onStatus: (status: AlertStreamStatus) => void
    fetchImpl?: typeof fetch
    timing?: Partial<AlertStreamTiming>
    random?: () => number
}

export interface AlertStreamClient {
    start: () => void
    stop: () => void
    /** 대기 중인 재접속을 지금 시도한다(탭 복귀·네트워크 복구) */
    nudge: () => void
}

type ConnectionResult =
    | { kind: 'reconnect'; stable: boolean }
    | { kind: 'ended'; stable: boolean }
    | { kind: 'unauthorized' }
    | { kind: 'denied' }
    | { kind: 'unavailable'; retryAfterMs: number }
    | { kind: 'failed' }

const parseRetryAfterMs = (value: string | null): number => {
    const seconds = value === null ? NaN : Number(value)
    return Number.isFinite(seconds) && seconds > 0 ? seconds * 1000 : 0
}

/**
 * 알림 SSE 접속 루프.
 *
 * - 서버가 보내는 `reconnect`(연결 수명 만료)는 정상 흐름이다.
 * - 401 은 토큰을 갱신해 다시 붙고, 갱신하지 못해도 로그아웃하지 않는다 — 폴링 경로가 세션 만료를 처리한다.
 * - 403 은 재시도하지 않는다(권한 없음). 404·429·503 은 폴링으로 두고 천천히 다시 시도한다.
 * - 조용히 끊긴 연결은 워치독이 잡는다.
 */
export const createAlertStreamClient = (options: AlertStreamClientOptions): AlertStreamClient => {
    const timing = { ...DEFAULT_ALERT_STREAM_TIMING, ...options.timing }
    const random = options.random ?? Math.random
    const doFetch = options.fetchImpl ?? ((...args) => fetch(...args))

    let running = false
    let stopped = false
    let activeController: AbortController | null = null
    let wake: (() => void) | null = null

    const emitStatus = (status: AlertStreamStatus) => {
        if (!stopped) options.onStatus(status)
    }

    const jittered = (ms: number) => Math.max(1_000, ms * (0.5 + random() * 0.5))

    const wait = (ms: number) =>
        new Promise<void>((resolve) => {
            const timer = setTimeout(done, ms)
            function done() {
                clearTimeout(timer)
                wake = null
                resolve()
            }
            wake = done
        })

    const connectOnce = async (): Promise<ConnectionResult> => {
        const token = options.getToken()
        if (!token) return { kind: 'denied' }

        const controller = new AbortController()
        activeController = controller

        let watchdog: ReturnType<typeof setTimeout> | undefined
        const armWatchdog = () => {
            if (watchdog) clearTimeout(watchdog)
            watchdog = setTimeout(() => controller.abort(), timing.watchdogMs)
        }

        try {
            armWatchdog()
            const response = await doFetch(options.url, {
                headers: { Accept: 'text/event-stream', Authorization: `Bearer ${token}` },
                credentials: 'include',
                cache: 'no-store',
                signal: controller.signal
            })

            if (response.status === 401) return { kind: 'unauthorized' }
            if (response.status === 403) return { kind: 'denied' }
            if (!response.ok || !response.body) {
                return {
                    kind: 'unavailable',
                    retryAfterMs: parseRetryAfterMs(response.headers.get('Retry-After'))
                }
            }

            const reader = response.body.getReader()
            const decoder = new TextDecoder()
            let connectedAt = 0
            let reconnectRequested = false
            let serverError = false

            const parser = createSseParser((message) => {
                if (stopped) return
                if (message.event === 'reconnect') {
                    reconnectRequested = true
                    return
                }
                if (message.event === 'error') {
                    serverError = true
                    return
                }
                try {
                    options.onEvent(message.event, JSON.parse(message.data))
                } catch {
                    // 해석할 수 없는 프레임은 무시한다
                }
            })

            for (;;) {
                const { done, value } = await reader.read()
                if (done) break
                if (stopped) {
                    await reader.cancel().catch(() => undefined)
                    break
                }
                armWatchdog()

                if (connectedAt === 0) {
                    connectedAt = Date.now()
                    emitStatus('connected')
                }
                parser.feed(decoder.decode(value, { stream: true }))

                if (reconnectRequested || serverError) {
                    await reader.cancel().catch(() => undefined)
                    break
                }
            }

            const stable = connectedAt > 0 && Date.now() - connectedAt >= timing.stableConnectionMs
            if (reconnectRequested) return { kind: 'reconnect', stable: true }
            return { kind: 'ended', stable }
        } catch {
            return { kind: 'failed' }
        } finally {
            if (watchdog) clearTimeout(watchdog)
            if (activeController === controller) activeController = null
        }
    }

    const loop = async () => {
        let failures = 0
        let refreshedForThisAttempt = false

        while (!stopped) {
            emitStatus(failures === 0 ? 'connecting' : 'retrying')
            const result = await connectOnce()
            if (stopped) break

            if (result.kind === 'unauthorized') {
                if (!refreshedForThisAttempt) {
                    refreshedForThisAttempt = true
                    const token = await options.refreshToken()
                    if (stopped) break
                    if (token) {
                        options.onToken(token)
                        continue
                    }
                }
                emitStatus('unavailable')
                refreshedForThisAttempt = false
                await wait(jittered(timing.unavailableDelayMs))
                continue
            }
            refreshedForThisAttempt = false

            if (result.kind === 'denied') {
                emitStatus('denied')
                break
            }

            if (result.kind === 'unavailable') {
                emitStatus('unavailable')
                await wait(jittered(Math.max(timing.unavailableDelayMs, result.retryAfterMs)))
                continue
            }

            if (result.kind === 'reconnect') {
                failures = 0
                await wait(random() * timing.reconnectJitterMs)
                continue
            }

            failures = result.kind === 'ended' && result.stable ? 1 : failures + 1
            emitStatus('retrying')
            const backoff = Math.min(
                timing.backoffMaxMs,
                timing.backoffBaseMs * 2 ** Math.min(failures - 1, 10)
            )
            await wait(jittered(backoff))
        }
    }

    return {
        start: () => {
            if (running) return
            running = true
            stopped = false
            void loop().finally(() => {
                running = false
            })
        },
        stop: () => {
            if (stopped) return
            stopped = true
            options.onStatus('stopped')
            activeController?.abort()
            wake?.()
        },
        nudge: () => {
            if (!stopped) wake?.()
        }
    }
}
